// apps/main/client/src/lib/turnstileClient.js — RUN TU. The CLIENT half of Cloudflare Turnstile.
//
// ═══════════════════════════════════════════════════════════════════════════════════════════════
// WHAT THIS IS, AND WHY IT IS ONE FILE SHARED BY TWO BUNDLERS.
//
// `apps/main/server/lib/turnstile.js` has been the whole story since RUN AB: it verifies a token as
// GATE 1 of `clarkMeter.checkClarkCap()`, before spend and before the visitor's trial, and it fails
// closed on a missing one. It has never had a counterpart. RUN TS measured that directly — zero
// occurrences of `TURNSTILE_SITE_KEY`, `cf-turnstile` or `challenges.cloudflare.com` anywhere in
// the client trees, and zero matches for "turnstile" in either shipped production bundle. The
// server has been asking a question the browser was never taught to answer.
//
// 🔴 THE ANONYMOUS TURN NOW HAPPENS IN TWO PLACES, AND A TOKEN CANNOT TRAVEL BETWEEN THEM.
// RUN CH3 made the homepage widget a LAUNCHER: it makes the FIRST anonymous call, then navigates
// to /clark carrying the answer, and /clark makes every SUBSEQUENT one. Both are gated by the same
// `verifyTurnstile()`. It is tempting to mint one token on the homepage and pass it through
// handoff.js's `home->clark` payload alongside the answer — and it would fail on the first turn
// after landing, every time. A Turnstile token is SINGLE-USE (Cloudflare answers a replay with
// `timeout-or-duplicate`) and lives ~300 s. The homepage's token is already spent by the time the
// navigation completes.
//
// So the challenge mounts on BOTH surfaces, each minting its own tokens, and this module is the one
// implementation both of them run — the homepage island through esbuild, the SPA through Vite.
// The handoff carries the ANSWER. It does not carry the token, and must not be made to.
//
// ── 🔴 THE SITE KEY: THE SERVER IS THE SOURCE, THE BUILD-ARG IS THE FALLBACK ───────────────────
//
// The site key and `TURNSTILE_SECRET_KEY` are a PAIR — a token minted for one site key does not
// verify against another key's secret. If they ever disagree, every anonymous turn is refused, and
// that is the exact outage §2 of this run exists to avoid. So the primary source is `GET
// /api/turnstile`, answered by the SAME PROCESS that holds the secret, from the same environment:
// the two cannot drift, because there is only one of them.
//
// `__CC_TURNSTILE_SITE_KEY__` is the build-time fallback, substituted by both bundlers (see
// vite.config.mjs and esbuild.config.mjs). It is a fallback and not the source deliberately: a key
// baked at image-build time is a snapshot of what the environment said THEN, and a rotated key
// would leave a correct-looking bundle pointed at a dead pair until someone rebuilt it.
//
// ── 🔴 THREE STATES, MIRRORING THE SERVER'S THREE, AND THE MIDDLE ONE IS THE WHOLE DESIGN ─────
//
//   1. UNCONFIGURED — no site key from either source. `getTurnstileToken()` returns
//      { state: "unconfigured", token: null } and the caller sends the request WITHOUT a token.
//      🔴 THIS IS THE ONE FAIL-OPEN, AND IT IS THE MIRROR OF turnstile.js's STATE 1. If a keyless
//      client refused instead, merging this would take the anonymous door dark the moment it
//      deployed — before anyone had set a key — which is a self-inflicted outage caused by
//      shipping a security control correctly. It is also what makes the §2 ORDER safe: this ships
//      while `TURNSTILE_SECRET_KEY` is unset, both halves are unconfigured, both allow, and the
//      demo keeps working until Adam sets the secret.
//   2. OK — a fresh token. Sent as `cf-turnstile-response`.
//   3. CONFIGURED BUT UNREACHABLE — a site key exists and Cloudflare could not be reached, or the
//      challenge produced no token inside the budget. { state: "unreachable", token: null } and
//      the caller REFUSES LOCALLY without sending the request at all. See anonFetch.js.
//
// 🔴 A SIGNED-IN VISITOR NEVER REACHES THIS MODULE. The gate is anonymous-only, so nothing here is
// invoked and the Cloudflare script is never even fetched for them — not merely "no challenge
// shown", but no third-party request at all. anonFetch.js is where that branch is taken.
// ═══════════════════════════════════════════════════════════════════════════════════════════════

/* global __CC_TURNSTILE_SITE_KEY__ */

/**
 * 🔴 THE ONLY THIRD-PARTY URL THIS PRODUCT'S OWN CODE FETCHES.
 * It is declared in `apps/main/test/servedBytesNoThirdParty.test.js` as a page-scoped
 * DISCLOSED_THIRD_PARTY entry, and that test now reads the committed island bundle as well as the
 * served HTML — precisely so that loading it from JS instead of from a `<script>` tag in the markup
 * does not hide it from the gate.
 */
const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

/** Where the site key comes from when the page can ask a server. Same process as the secret. */
const CONFIG_URL = "/api/turnstile";

/**
 * 🔴 THE FRESHNESS WINDOW, AND WHY IT IS NOT 300 SECONDS.
 *
 * Cloudflare expires an unredeemed token after ~300 s. Handing one back at 299 s means the round
 * trip to our server and its round trip to siteverify both have to complete inside a second, and a
 * visitor who loses that race gets a refusal they did nothing to earn — §3's "sat on the page for
 * ten minutes" case, failing at the fence instead of the field. 120 s leaves 180 s of margin for a
 * slow network and a slow siteverify, and re-minting is cheap: it is a challenge the visitor
 * already passes invisibly.
 */
const FRESH_MS = 120_000;

/**
 * How long we will wait for a SILENT token before calling Cloudflare unreachable. This is the
 * budget for the majority case: script fetch, challenge, callback, no interaction.
 */
const TOKEN_TIMEOUT_MS = 8_000;

/**
 * 🔴 AND THE BUDGET FOR A VISITOR WHO IS ACTUALLY SHOWN A CHECKBOX. MEASURED, AND IT WAS WRONG.
 *
 * Driven in a real browser against Cloudflare's forced-interactive test key
 * (3x00000000000000000000FF), the first version of this module rendered the "Verify you are human"
 * checkbox correctly — and then refused the turn 8 seconds later, with the checkbox still on
 * screen, unclicked. The visitor read "we couldn't complete the security check" while looking at
 * the security check they had not been given time to complete. A gate that refuses the people who
 * are cooperating with it is worse than no gate; it is the failure mode this whole design is
 * arranged to avoid, arriving through the one path nobody drives.
 *
 * `before-interactive-callback` fires when Turnstile is about to demand interaction, and from that
 * moment the deadline becomes this instead. 90 s is a human reading-and-clicking budget, not a
 * network budget, which is why it is an order of magnitude larger and why the two numbers are
 * separate rather than one compromise value that is too long for a timeout and too short for a
 * person.
 */
const INTERACTIVE_TIMEOUT_MS = 90_000;

/** How long we will wait for the script itself. Folded into the budget above, not added to it. */
const SCRIPT_TIMEOUT_MS = 6_000;

let _siteKeyPromise = null;
let _scriptPromise = null;
let _widgetId = null;
let _renderPromise = null;
let _token = null;
let _tokenMintedAt = 0;
let _pending = null;          // resolve fn for the callback that is currently being awaited
let _interactiveSince = 0;    // when Turnstile last said it needs the visitor to act
let _warnedUnconfigured = false;

/** The build-time literal, if either bundler substituted one. Never throws when undefined. */
function bakedSiteKey() {
  try {
    // eslint-disable-next-line no-undef
    return typeof __CC_TURNSTILE_SITE_KEY__ === "string" ? __CC_TURNSTILE_SITE_KEY__.trim() : "";
  } catch {
    return "";
  }
}

/**
 * The site key: the server first, the baked value second, "" for unconfigured. Resolved at most
 * once per page and never rejects — an unreachable config endpoint is simply "no key from there".
 */
export function siteKey() {
  if (_siteKeyPromise) return _siteKeyPromise;
  _siteKeyPromise = (async () => {
    try {
      const ctrl = typeof AbortController === "function" ? new AbortController() : null;
      const t = ctrl ? setTimeout(() => ctrl.abort(), 3_000) : null;
      try {
        const res = await fetch(CONFIG_URL, { headers: { accept: "application/json" },
                                              ...(ctrl ? { signal: ctrl.signal } : {}) });
        if (res.ok) {
          const data = await res.json().catch(() => null);
          const k = typeof data?.siteKey === "string" ? data.siteKey.trim() : "";
          if (k) return k;
        }
      } finally { if (t) clearTimeout(t); }
    } catch { /* the server could not say; fall through to the baked value */ }
    return bakedSiteKey();
  })();
  return _siteKeyPromise;
}

function warnUnconfiguredOnce() {
  if (_warnedUnconfigured) return;
  _warnedUnconfigured = true;
  // Deliberately console.warn and not console.error: this is the DESIGNED state 1, not a fault.
  console.warn(
    "[turnstile] no site key (GET /api/turnstile returned none and no build-arg was baked) — " +
    "anonymous turns are being sent UNCHALLENGED. Set TURNSTILE_SITE_KEY on the server, or pass " +
    "--build-arg VITE_TURNSTILE_SITE_KEY, to close the client half.",
  );
}

/** Inject the Cloudflare script once. Resolves to `window.turnstile`, or null if it never arrives. */
function loadScript() {
  if (_scriptPromise) return _scriptPromise;
  _scriptPromise = new Promise((resolve) => {
    if (typeof document === "undefined") return resolve(null);
    if (window.turnstile) return resolve(window.turnstile);

    let settled = false;
    const done = (v) => { if (!settled) { settled = true; resolve(v); } };
    const timer = setTimeout(() => done(null), SCRIPT_TIMEOUT_MS);

    let el = document.querySelector('script[data-cc-turnstile="1"]');
    if (!el) {
      el = document.createElement("script");
      el.src = SCRIPT_URL;
      el.async = true;
      el.defer = true;
      el.setAttribute("data-cc-turnstile", "1");
      document.head.appendChild(el);
    }
    el.addEventListener("load", () => {
      clearTimeout(timer);
      // `render=explicit` means the API is usable only after turnstile.ready().
      try { window.turnstile.ready(() => done(window.turnstile)); }
      catch { done(window.turnstile || null); }
    });
    // A blocked host, an offline visitor, a content blocker: all land here. State 3.
    el.addEventListener("error", () => { clearTimeout(timer); done(null); });
  });
  return _scriptPromise;
}

/* ══ 🔴 RUN CK §5 — THE CHALLENGE MOVES INTO THE MODAL, AND THERE IS STILL ONLY ONE OF IT. ══════
 *
 * Adam's ruling: no Turnstile widget renders inline anywhere; verification happens inside the
 * product's blue modal. This does NOT re-render the widget into the modal — `turnstile.render()`
 * is called exactly once per page (see `ensureWidget`), and a second call against the same sitekey
 * is what mints the `timeout-or-duplicate` refusals this module's cache exists to avoid. The one
 * host NODE is moved, so the live challenge and any in-flight interaction survive the move.
 *
 * `emit` is a DOM event rather than a callback registry for the same reason researchGate.js uses
 * one: this module is imported by the SPA and by the marketing island, and a surface that is not
 * mounted simply does not listen.
 */
function emit(phase) {
  try {
    window.dispatchEvent(new CustomEvent("cc:turnstile", { detail: { phase } }));
  } catch { /* no CustomEvent — the challenge still works, it just has no modal */ }
}

/** Move the one host into `container` (the modal's slot). Idempotent. */
export function attachTurnstileHost(container) {
  const el = host();
  if (!container || el.parentElement === container) return el;
  // In the modal the slot does the positioning; the fixed anchoring below is for the page.
  el.style.position = "static";
  el.style.left = ""; el.style.bottom = ""; el.style.transform = ""; el.style.zIndex = "";
  container.appendChild(el);
  return el;
}

/** Put it back on the page, where it is invisible until it is needed again. */
export function detachTurnstileHost() {
  const el = document.getElementById("cc-turnstile-host");
  if (!el || el.parentElement === document.body) return;
  el.style.position = "fixed";
  el.style.left = "50%";
  el.style.bottom = "16px";
  el.style.transform = "translateX(-50%)";
  el.style.zIndex = "2147483000";
  document.body.appendChild(el);
}

/**
 * The host element the challenge renders into.
 *
 * 🔴 IT IS ON-SCREEN, NOT OFF-SCREEN, AND THAT MATTERS FOR THE MINORITY CASE. `appearance:
 * "interaction-only"` means Cloudflare renders NOTHING — a zero-height, empty widget — for the
 * visitors who pass silently, which is nearly all of them. The remainder are shown a checkbox, and
 * a checkbox parked at `left: -9999px` is a visitor who can never complete a challenge and never
 * finds out why. Fixed to the bottom of the viewport, above everything, so the rare interactive
 * case is visible and clickable wherever it fires from.
 *
 * No colours are set here — the widget paints itself, and the host is a transparent positioner.
 */
function host() {
  let el = document.getElementById("cc-turnstile-host");
  if (el) return el;
  el = document.createElement("div");
  el.id = "cc-turnstile-host";
  el.setAttribute("aria-live", "polite");
  el.style.position = "fixed";
  el.style.left = "50%";
  el.style.bottom = "16px";
  el.style.transform = "translateX(-50%)";
  el.style.zIndex = "2147483000";
  document.body.appendChild(el);
  return el;
}

/** Render the widget once. Resolves to the widget id, or null when Cloudflare is unreachable. */
function ensureWidget(key) {
  if (_renderPromise) return _renderPromise;
  _renderPromise = (async () => {
    const ts = await loadScript();
    if (!ts) return null;
    try {
      _widgetId = ts.render(host(), {
        sitekey: key,
        // MANAGED, INVISIBLE FOR MOST. "managed" is the dashboard-side mode; `interaction-only`
        // is the rendering half of it — no branded box for a visitor who passes silently, and a
        // real checkbox for the one who does not.
        appearance: "interaction-only",
        size: "flexible",
        // The token arrives here. Every mint routes through `settle()` so a token produced by an
        // automatic refresh is cached exactly like one produced by a reset we asked for.
        callback: (token) => settle(token),
        "expired-callback": () => { _token = null; _tokenMintedAt = 0; },
        // 🔴 THE VISITOR IS ABOUT TO BE ASKED TO DO SOMETHING. From here the deadline is a human
        // one, not a network one — see INTERACTIVE_TIMEOUT_MS.
        /* 🔴 RUN CK §5 — THESE TWO NOW ANNOUNCE THEMSELVES, AND THAT IS WHAT PUTS THE CHALLENGE
           IN A MODAL INSTEAD OF ON THE PAGE. `appearance: "interaction-only"` renders NOTHING for
           the visitors who pass silently — so a modal opened for everyone would be pure friction on
           the one moment §5.3 says must not gain any ("no second click"). It opens exactly when
           Cloudflare says it is about to ask the visitor for something, and closes when it stops. */
        "before-interactive-callback": () => {
          _interactiveSince = Date.now();
          emit("interactive");
        },
        "after-interactive-callback": () => {
          _interactiveSince = 0;
          emit("done");
        },
        // 🔴 THESE TWO ARE STATE 3. Returning false from error-callback tells Turnstile not to
        // retry forever behind our back; the caller's timeout is what turns this into a refusal.
        "error-callback": () => { settle(null); emit("failed"); return false; },
        "timeout-callback": () => { settle(null); emit("failed"); },
        "refresh-expired": "auto",
        "response-field": false, // there is no <form> here; we read the token in JS.
      });
      return _widgetId;
    } catch {
      return null;
    }
  })();
  return _renderPromise;
}

function settle(token) {
  if (typeof token === "string" && token) {
    _token = token;
    _tokenMintedAt = Date.now();
  }
  _interactiveSince = 0;
  const p = _pending;
  _pending = null;
  if (p) p(typeof token === "string" && token ? token : null);
}

/**
 * Ask the rendered widget for a new token, bounded. Resolves to a token or null.
 *
 * The deadline is not a single `setTimeout`, because it MOVES: a challenge that turns interactive
 * while we are waiting replaces the network budget with a human one. Polled rather than
 * re-armed so there is one place that owns the deadline and no timer to forget to clear.
 */
function mint(ts, budgetMs) {
  return new Promise((resolve) => {
    let settled = false;
    let deadline = Date.now() + budgetMs;
    const done = (v) => { if (!settled) { settled = true; resolve(v); } };
    const tick = setInterval(() => {
      if (_interactiveSince) deadline = Math.max(deadline, _interactiveSince + INTERACTIVE_TIMEOUT_MS);
      if (Date.now() >= deadline) {
        clearInterval(tick);
        if (_pending === handoff) _pending = null;
        done(null);
      }
    }, 250);
    const handoff = (v) => { clearInterval(tick); done(v); };
    _pending = handoff;
    _interactiveSince = 0;
    try { ts.reset(_widgetId); } catch { clearInterval(tick); _pending = null; done(null); }
  });
}

/**
 * A token for ONE anonymous request.
 *
 * @returns {Promise<{state: "unconfigured"|"ok"|"unreachable", token: string|null}>}
 *
 * 🔴 EVERY RETURNED TOKEN IS CONSUMED FROM THE CACHE. Turnstile tokens are single-use; handing the
 * same one to two requests refuses the second with `timeout-or-duplicate`. The cache exists only so
 * that the token minted while the page was idle can serve the turn the visitor is about to take.
 *
 * 🔴 AND THIS IS THE ANSWER TO "SAT ON THE PAGE FOR TEN MINUTES". A cached token older than
 * FRESH_MS is not returned — it is discarded and the widget is reset for a new one, which for the
 * silent majority costs no interaction and a few hundred milliseconds. Cloudflare's own
 * `expired-callback` clears the cache too, but it is not relied on alone: it fires on the widget's
 * schedule, and a page that was backgrounded for the whole window may not have run it yet.
 */
export async function getTurnstileToken({ timeoutMs = TOKEN_TIMEOUT_MS } = {}) {
  const key = await siteKey();
  if (!key) { warnUnconfiguredOnce(); return { state: "unconfigured", token: null }; }

  const started = Date.now();
  if (_token && Date.now() - _tokenMintedAt < FRESH_MS) {
    const t = _token;
    _token = null; _tokenMintedAt = 0;
    return { state: "ok", token: t };
  }
  _token = null; _tokenMintedAt = 0;

  const id = await ensureWidget(key);
  const ts = window.turnstile;
  if (id === null || id === undefined || !ts) return { state: "unreachable", token: null };

  const remaining = Math.max(1_000, timeoutMs - (Date.now() - started));
  const token = await mint(ts, remaining);
  if (!token) return { state: "unreachable", token: null };
  _token = null; _tokenMintedAt = 0; // consumed
  return { state: "ok", token };
}

/* 🔴 THERE IS DELIBERATELY NO BACKGROUND PRE-MINT. An earlier draft kept a spare token warm so a
   second turn would not wait for a challenge. It buys almost nothing — the anonymous grant is one
   turn per rolling day, so a second anonymous turn is refused by gate 3 whatever the token says —
   and it costs something real: with `appearance: "interaction-only"`, a pre-mint that Cloudflare
   decides to challenge pops a "Verify you are human" checkbox onto the page while the visitor is
   doing nothing and has asked for nothing. A challenge must be the consequence of an action. */

/** Test seam. Resets every module-level cache so a suite can drive each state in isolation. */
export function __resetForTests() {
  _siteKeyPromise = null; _scriptPromise = null; _widgetId = null; _renderPromise = null;
  _token = null; _tokenMintedAt = 0; _pending = null; _interactiveSince = 0;
  _warnedUnconfigured = false;
}

export { SCRIPT_URL, CONFIG_URL, FRESH_MS, TOKEN_TIMEOUT_MS, INTERACTIVE_TIMEOUT_MS };
